import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  dropdownFetch,
  getCurrentDateTime,
  getKeyPathValue,
  request,
  toast
} from '../../../../../utils';
import { BASE_URL } from '../../../../../utils';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { GENDER_DROPDOWN, RECEIPT_MODE, TITLES, LAB_BOOKING_TYPE } from '../../../../../constants';
import { useLocation } from 'react-router-dom';
import { IPDSearchHandler } from '../../HMS/transactions/utils';
import { CustomButtonn } from '../../../../../components';
import { AgeCalculator } from '../../../../../utils/AgeCalculator';
import { PhysiotherapyBookingDetailForm } from './PhysiotherapyBookingDetailForm';

const DISCOUNT_TYPES = [
  { _id: 'amount', name: 'Amount' },
  { _id: 'percentage', name: 'Percentage' }
];

const QUERY_TABLE_HEADER = [
  {
    header: 'Bill No',
    accessor: (row) => (
      <span style={{ color: row.status === 'inactive' ? 'red' : 'black' }}>
        {row.bill_no}
      </span>
    )
  },
  { header: 'Bill Date', accessor: (row) => getCurrentDateTime('date', row.createdAt) },
  { header: 'UHID No', accessor: 'uhid' },
  { header: 'Patient Name', accessor: 'patient_name' },
  { header: 'Mobile No', accessor: 'mobile' },
  { header: 'Net Amount', accessor: 'net_amt' },
  { header: 'Status', accessor: 'status', sx: { textTransform: 'uppercase' } }
];

const BILL_TABLE_HEADER = [
  { header: 'Service Name', accessor: 'service_name' },
  { header: 'Qty', accessor: 'quantity' },
  { header: 'Rate', accessor: 'rate' },
  { header: 'Amount', accessor: 'amount' },
  { header: 'Dis. Amt.', accessor: 'disc_amt' },
  { header: 'Net Amt', accessor: 'net_amt' }
];

const PATIENT_TABLE_HEADER = [
  { header: 'UHID', accessor: 'uhid' },
  { header: 'Patient Name', accessor: (row) => `${row.name_suffix || ''} ${row.first_name || ''} ${row.last_name || ''}`.trim() },
  { header: 'Mobile No', accessor: 'address.mobile' },
  { header: 'Doctor', accessor: 'consultant.name' },
  { header: 'Address', accessor: 'address.address' }
];

const initialDataObj = { data: [], loading: false };

export const PhysiotherapyBooking = () => {
  const sponsorIdRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];

  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [bookingDataObj, setBookingDataObj] = useState(initialDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [searchPatientDialog, setSearchPatientDialog] = useState(false);
  const [patientDetailList, setPatientDetailList] = useState([]);
  const [IPDSearchDialogOpen, setIPDSearchDialogOpen] = useState(false);
  const [guardianDropdown, setGuardianDropdown] = useState([]);
  const [sponsorDropdown, setSponsorDropdown] = useState([]);
  const [referralDropdown, setReferralDropdown] = useState([]);
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [input, setInput] = useState('');
  const [patientType, setPatientType] = useState('walkin');
  const [discountType, setDiscountType] = useState('amount');

  const user = JSON.parse(localStorage.getItem('user'));
  const netAmtRef = useRef(0);
  const isMounted = useRef(true);

  const isEnabledDate =
    user?.permissions?.some((p) => p.includes('DatePermission')) ?? false;

  useEffect(() => () => { isMounted.current = false; }, []);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        patient_name: Yup.string().required('Please enter patient name'),
        age: Yup.string().required('Please enter age'),
        mobile: Yup.string()
          .required('Mobile number is required')
          .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits'),
        bill: Yup.object().shape({
          pay_mode: Yup.string().required('Payment mode is required')
        })
      })
    ),
    defaultValues: {
      discountType: 'amount',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      patient_suffix: 'Mr.',
      gender: 'Male',
      age: '',
      age_in_days: 0,
      booking_mode: 'Lab',
      bill: {
        pay_mode: 'CASH',
        rec_amt_1: 0,
        rec_amt_2: 0,
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0,
        total_qty: 0,
        total_amt: 0,
        total_recpt: 0,
        balance_amt: 0
      }
    },
    mode: 'all'
  });

  const { handleSubmit, watch, reset, setValue, formState } = methods;

  const discAmt = watch('bill.disc_amt');
  const netAmtWatch = watch('bill.net_amt');
  const recAmt1Watch = watch('bill.rec_amt_1');
  const recAmt2Watch = watch('bill.rec_amt_2');
  const payMode = watch('bill.pay_mode');
  const uhid = watch('uhid');
  const ipd_no = watch('ipd_no');
  const bookingModeWatch = watch('booking_mode');
  const sponsor = watch('sponsor');

  // Totals from detail list
  useEffect(() => {
    if (!isMounted.current) return;
    let total_qty = 0, total_amt = 0, gst_amt = 0, net_amt = 0;
    detailList.forEach((d) => {
      total_qty += Number(d.quantity) || 0;
      total_amt += Number(d.amount) || 0;
      gst_amt += Number(d.gst_amt) || 0;
      net_amt += Number(d.net_amt) || 0;
    });
    setValue('bill.total_qty', total_qty);
    setValue('bill.total_amt', total_amt);
    setValue('bill.gst_amt', gst_amt);
    setValue('bill.net_amt', net_amt);
    netAmtRef.current = net_amt;
  }, [detailList, setValue]);

  // Discount calculation
  useEffect(() => {
    if (!isMounted.current) return;
    const gross = netAmtRef.current;
    const discountAmount = Number(discAmt) || 0;
    if (gross > 0) {
      let calculatedNet = gross;
      if (discountType === 'percentage') {
        if (discountAmount > 0 && discountAmount <= 100) {
          calculatedNet = Math.max(0, gross - (gross * discountAmount) / 100);
        } else if (discountAmount > 100) {
          calculatedNet = 0;
          setValue('bill.disc_amt', 100);
        }
      } else {
        if (discountAmount > gross) {
          calculatedNet = 0;
          setValue('bill.disc_amt', gross);
        } else {
          calculatedNet = Math.max(0, gross - discountAmount);
        }
      }
      setValue('bill.net_amt', calculatedNet);
    }
  }, [discAmt, discountType, setValue]);

  // Payment mode / receipt calculation
  useEffect(() => {
    if (!isMounted.current) return;
    const net = Number(netAmtWatch) || 0;
    const r1 = Number(recAmt1Watch) || 0;
    const r2 = Number(recAmt2Watch) || 0;
    if (payMode === 'CASH') {
      setValue('bill.rec_amt_2', 0);
      const capped = Math.min(r1, net);
      setValue('bill.rec_amt_1', r1 > net ? net : r1);
      setValue('bill.total_recpt', r1 > net ? net : r1);
      setValue('bill.balance_amt', Math.max(0, net - (r1 > net ? net : r1)));
    } else if (['Bank', 'UPI', 'Paytm', 'RTGS/NEFT/Cheque', 'Credit Card', 'Debit Card'].includes(payMode)) {
      setValue('bill.rec_amt_1', 0);
      setValue('bill.rec_amt_2', net);
      setValue('bill.total_recpt', net);
      setValue('bill.balance_amt', 0);
    } else if (payMode === 'Credit') {
      setValue('bill.rec_amt_1', 0);
      setValue('bill.rec_amt_2', 0);
      setValue('bill.total_recpt', 0);
      setValue('bill.balance_amt', net);
    } else {
      setValue('bill.total_recpt', r1 + r2);
      setValue('bill.balance_amt', Math.max(0, net - r1 - r2));
    }
  }, [netAmtWatch, recAmt1Watch, recAmt2Watch, payMode, setValue]);

  // Load selected record
  useEffect(() => {
    if (!isMounted.current || !selectedRecord) return;
    const keyValuePair = getKeyPathValue(selectedRecord, 'bill', ['__v', '_id', 'createdAt', 'status', 'service_items', 'uhid']);
    keyValuePair.forEach(({ key, value }) => setValue(key, value));
    setValue('uhid', selectedRecord.uhid || '');
    setValue('bill_no', selectedRecord.bill_no || '');
    setValue('patient_suffix', selectedRecord.patient_suffix || 'Mr.');
    setValue('patient_name', selectedRecord.patient_name || '');
    setValue('mobile', selectedRecord.mobile || '');
    setValue('address', selectedRecord.address || '');
    setValue('ref_by', selectedRecord.ref_by?._id || selectedRecord.ref_by || '');
    setValue('sponsor', selectedRecord.sponsor?._id || selectedRecord.sponsor || '');
    setValue('doctor', selectedRecord.doctor?._id || selectedRecord.doctor || '');
    setValue('remarks', selectedRecord.remarks || '');
    setValue('guardian_type', selectedRecord.guardian_type || '');
    setValue('guardian_name', selectedRecord.guardian_name || '');
    setValue('age', selectedRecord.age || '');
    setValue('booking_mode', selectedRecord.booking_mode || 'Lab');
    const billDate = selectedRecord.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setValue('date', billDate);
    setValue('time', selectedRecord.billing_time || new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5));
    setDetailList(selectedRecord.service_items || []);
    setPatientType(selectedRecord.patient_type || 'walkin');
  }, [selectedRecord, setValue]);

  // Dropdowns
  const getGuardianDropdown = useCallback(async () => {
    const data = await dropdownFetch('relation-master');
    if (isMounted.current) {
      setGuardianDropdown(data || []);
      if (!selectedRecord) {
        const def = data.find(({ status }) => status === 'default');
        if (def) setValue('guardian_type', def._id);
      }
    }
  }, [selectedRecord, setValue]);

  const getSponsorDropdown = useCallback(async () => {
    const data = await dropdownFetch('sponsor');
    if (isMounted.current) {
      setSponsorDropdown(data || []);
      if (!selectedRecord) {
        const def = data.find(({ status }) => status === 'default');
        if (def) setValue('sponsor', def._id);
      }
    }
  }, [selectedRecord, setValue]);

  const getReferralDropdown = useCallback(async () => {
    const data = await dropdownFetch('referral');
    if (isMounted.current) {
      setReferralDropdown(data || []);
      if (!selectedRecord) {
        const def = data.find(({ status }) => status === 'default');
        if (def) setValue('ref_by', def._id);
      }
    }
  }, [selectedRecord, setValue]);

  const getDoctorDropdown = useCallback(async () => {
    const data = await dropdownFetch('doctor');
    if (isMounted.current) setDoctorDropdown(data || []);
  }, []);

  useEffect(() => {
    getGuardianDropdown();
    getSponsorDropdown();
    getReferralDropdown();
    getDoctorDropdown();
  }, [getGuardianDropdown, getSponsorDropdown, getReferralDropdown, getDoctorDropdown]);

  // Booking mode change
  const handleBookingModeChange = (mode) => {
    if (mode === 'Lab') {
      setValue('uhid', '');
      setValue('ipd_no', '');
      setPatientType('walkin');
      ['patient_name', 'mobile', 'address', 'doctor', 'age'].forEach((f) => setValue(f, ''));
      setValue('gender', 'Male');
      setValue('age_in_days', 0);
    } else if (mode === 'Opd') {
      setValue('ipd_no', '');
      setPatientType('OPD');
    } else if (mode === 'Ipd') {
      setValue('uhid', '');
      setPatientType('IPD');
    }
  };

  // Search patient by UHID
  const searchPatientHandler = async (uhidVal = null) => {
    setLoading(true);
    try {
      if (!uhidVal) {
        setSearchPatientDialog(true);
        const res = await request('/patient-registeration-search', { method: 'GET', query: { limit: 100 } });
        if (res?.success) setPatientDetailList((res.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        return;
      }
      const res = await request('/patient-registeration-search', { method: 'GET', query: { uhid: uhidVal } });
      if (res?.success && res.data?.length > 0) {
        const p = res.data[0];
        setPatientType('OPD');
        setValue('uhid', p.uhid || '');
        setValue('patient_name', `${p.first_name || ''} ${p.last_name || ''}`.trim());
        setValue('patient_suffix', p.name_suffix || 'Mr.');
        setValue('mobile', p.address?.mobile || '');
        setValue('gender', p.gender || 'Male');
        setValue('address', p.address?.address || '');
        setValue('doctor', p.consultant?._id || '');
        setValue('ref_by', p.ref_by?._id || '');
        setValue('sponsor', p.sponsor?._id || '');
        setValue('guardian_name', p.guardian_name || '');
        setValue('guardian_type', p.guardian_type?._id || '');
        if (p.dob) {
          const age = AgeCalculator.calculatePreciseAge(p.dob);
          setValue('age', age?.displayText || '');
          setValue('age_in_days', age?.totalDays || 0);
        }
        toast('Patient loaded', { variant: 'success' });
      } else {
        toast('No patient found', { variant: 'warning' });
      }
    } catch {
      toast('Error searching patient', { variant: 'error' });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // Search IPD
  const searchIPDDetails = async (ipd_no) => {
    setLoading(true);
    try {
      const res = await request('/indoor-registeration-detail', { method: 'GET', params: ipd_no });
      if (res?.success && res.data) {
        const p = res.data;
        setPatientType('IPD');
        setValue('ipd_no', ipd_no);
        setValue('patient_name', `${p.patient?.first_name || ''} ${p.patient?.last_name || ''}`.trim());
        setValue('patient_suffix', p.patient?.name_suffix || 'Mr.');
        setValue('uhid', p.uhid || '');
        setValue('doctor', p.patient?.consultant?._id || '');
        setValue('mobile', p.patient?.address?.mobile || '');
        setValue('gender', p.patient?.gender || 'Male');
        setValue('address', p.patient?.address?.address || '');
        setValue('sponsor', p.patient?.sponsor?._id || '');
        sponsorIdRef.current = p.patient?.sponsor?._id;
        if (p.patient?.dob) {
          const age = AgeCalculator.calculatePreciseAge(p.patient.dob);
          setValue('age', age?.displayText || '');
          setValue('age_in_days', age?.totalDays || 0);
        }
      } else {
        toast('No IPD record found', { variant: 'warning' });
      }
    } catch {
      toast('Error loading IPD', { variant: 'error' });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // Form submit
  const formSubmit = async (formValues) => {
    if (!isMounted.current) return;
    if (detailList.length === 0) {
      toast('Please add at least one service', { variant: 'warning' });
      return;
    }
    setLoading(true);

    let finalDiscAmt = Number(formValues.bill.disc_amt) || 0;
    if (discountType === 'percentage' && finalDiscAmt > 0 && finalDiscAmt <= 100) {
      finalDiscAmt = (netAmtRef.current * finalDiscAmt) / 100;
    }

    const isEdit = selectedRecord && currentAction === 'edit';
    const payload = {
      ...formValues,
      bill: { ...formValues.bill, disc_amt: finalDiscAmt },
      service_items: detailList,
      patient_type: formValues.booking_mode === 'Lab' ? 'walkin' : formValues.booking_mode === 'Opd' ? 'opd' : 'ipd',
      organisation,
      createdBy: user?.name || 'System',
      updatedBy: isEdit ? user?.name : ''
    };

    const response = await request(
      isEdit ? '/physiotherapy-booking-edit' : '/physiotherapy-booking-create',
      { method: isEdit ? 'PUT' : 'POST', params: isEdit ? selectedRecord._id : null, body: payload }
    );

    if (isMounted.current) {
      if (response?.success) {
        toast(`Successfully ${isEdit ? 'edited' : 'created'} Physiotherapy Booking`, { variant: 'success' });
        setSelectedRecord(response.data);
        if (!isEdit) {
          setDetailList([]);
          setDiscountType('amount');
          setPatientType('walkin');
          reset({
            discountType: 'amount',
            date: new Date().toISOString().split('T')[0],
            time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
            patient_suffix: 'Mr.',
            gender: 'Male',
            age: '',
            age_in_days: 0,
            booking_mode: 'Lab',
            bill: { pay_mode: 'CASH', rec_amt_1: 0, rec_amt_2: 0, tds_amt: 0, disc_amt: 0, gst_amt: 0, net_amt: 0, total_qty: 0, total_amt: 0, total_recpt: 0, balance_amt: 0 }
          });
        }
        setCurrentAction('');
      } else {
        toast(`Failed to ${isEdit ? 'edit' : 'create'} Physiotherapy Booking`, { variant: 'error' });
      }
      setLoading(false);
    }
  };

  const handleCancelRecord = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    const res = await request('/physiotherapy-booking-cancel', { method: 'DELETE', params: selectedRecord._id });
    if (res?.success) {
      toast('Successfully cancelled booking', { variant: 'success' });
      setSelectedRecord(null);
      reset();
    } else {
      toast('Failed to cancel booking', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    const res = await request('/physiotherapy-booking-delete', { method: 'DELETE', params: selectedRecord._id });
    if (res?.success) {
      toast('Successfully deleted booking', { variant: 'success' });
      setSelectedRecord(null);
      reset();
    } else {
      toast('Failed to delete booking', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setBookingDataObj({ loading: true, data: [] });
    try {
      const res = await axios.get(`${BASE_URL}/physiotherapy-booking/list?limit=100`);
      if (res.data?.success) {
        const filtered = (res.data.data || [])
          .filter((i) => ['active', 'inactive', 'default'].includes(i.status))
          .sort((a, b) => (b.bill_no || 0) - (a.bill_no || 0));
        setBookingDataObj({ loading: false, data: filtered });
      } else {
        setBookingDataObj({ loading: false, data: [] });
      }
    } catch {
      setBookingDataObj({ loading: false, data: [] });
    }
  };

  const handleResetForm = () => {
    setSelectedRecord(null);
    setCurrentAction('new');
    setDetailList([]);
    setPatientType('walkin');
    setDiscountType('amount');
    reset({
      discountType: 'amount',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      patient_suffix: 'Mr.',
      gender: 'Male',
      age: '',
      age_in_days: 0,
      booking_mode: 'Lab',
      bill: { pay_mode: 'CASH', rec_amt_1: 0, rec_amt_2: 0, tds_amt: 0, disc_amt: 0, gst_amt: 0, net_amt: 0, total_qty: 0, total_amt: 0, total_recpt: 0, balance_amt: 0 }
    });
  };

  // Query search
  useEffect(() => {
    if (!isMounted.current || !input.trim()) return;
    const timer = setTimeout(async () => {
      const res = await request('/physiotherapy-booking-list', { method: 'GET', query: { patient_name: input.toLowerCase().trim() } });
      if (res?.success && Array.isArray(res.data)) {
        setBookingDataObj({ loading: false, data: res.data });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [input]);

  const isFieldEditable = (uhid || ipd_no) ? false : ['new', 'edit'].includes(currentAction);

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container sx={{ mt: 1, width: '100%', maxWidth: 'none', minWidth: '100%', boxSizing: 'border-box' }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Physiotherapy Booking
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn id={`${lastPathSegment}Write`} variant="contained" disabled={['edit', 'new'].includes(currentAction)} onClick={handleResetForm}>New</CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Update`} variant="contained" disabled={['new', 'edit'].includes(currentAction) || !selectedRecord} onClick={() => setCurrentAction('edit')}>Edit</CustomButtonn>
                <CustomButtonn id="Submit" type="submit" variant="contained" disabled={!['edit', 'new'].includes(currentAction) || loading}>{loading ? 'Saving...' : 'Save'}</CustomButtonn>
                <CustomButtonn id="Cancel" variant="contained" disabled={!selectedRecord || !!currentAction} onClick={handleCancelRecord}>Cancel</CustomButtonn>
                <CustomButtonn id="isSuperAdmin" variant="contained" disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading} onClick={handleDeleteRecord}>Delete</CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Read`} variant="contained" onClick={handleQueryWindow} disabled={['new', 'edit'].includes(currentAction)}>Query</CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained" onClick={() => selectedRecord && window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')} disabled={!selectedRecord || !!currentAction}>Print</CustomButtonn>
                <CustomButtonn id="Exit" variant="contained" onClick={() => setCurrentAction('')}>Exit</CustomButtonn>
              </Stack>
            </Stack>

            <Box sx={{ padding: 2, borderRadius: '8px', width: '100%', boxShadow: '0px 2px 10px #0099ff' }}>
              <Grid container spacing={2}>
                {/* Column 1 */}
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Booking Mode</Typography>
                    <SelectTag formControlSX={{ flex: 1 }} name="booking_mode" disabled={!['new'].includes(currentAction)} menus={LAB_BOOKING_TYPE} onChange={(e) => handleBookingModeChange(e.target.value)} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>UHID / IPD No.</Typography>
                    <TextField disabled sx={{ flex: 0.3 }} name="uhid" placeholder="UHID" />
                    <Button disabled={!['new'].includes(currentAction) || bookingModeWatch !== 'Opd'} sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }} variant="contained" onClick={() => searchPatientHandler()}>
                      <SearchIcon />
                    </Button>
                    <TextField disabled sx={{ flex: 0.3 }} name="ipd_no" placeholder="IPD No." />
                    <Button disabled={!['new'].includes(currentAction) || bookingModeWatch !== 'Ipd'} sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }} variant="contained" onClick={() => setIPDSearchDialogOpen(true)}>
                      <SearchIcon />
                    </Button>
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Gender / Age</Typography>
                    <SelectTag formControlSX={{ flex: 0.4 }} name="gender" menus={GENDER_DROPDOWN} disabled={!isFieldEditable} />
                    <TextField name="age" sx={{ flex: 0.4 }} disabled={!isFieldEditable} placeholder="Age" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Sponsor</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="sponsor" disabled={!isFieldEditable} menus={sponsorDropdown} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Remarks</Typography>
                    <TextField sx={{ flex: 0.8 }} name="remarks" disabled={!['new', 'edit'].includes(currentAction)} />
                  </Stack>
                </Grid>

                {/* Column 2 */}
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Bill No</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="bill_no" placeholder="Bill No." />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Patient Name</Typography>
                    <SelectTag name="patient_suffix" formControlSX={{ flex: 0.2 }} disabled={!isFieldEditable} menus={TITLES} />
                    <TextField name="patient_name" sx={{ flex: 0.6 }} disabled={!isFieldEditable} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Mobile No</Typography>
                    <TextField sx={{ flex: 0.8 }} name="mobile" disabled={!isFieldEditable} type="number" error={!!formState.errors.mobile} helperText={formState.errors.mobile?.message || ''} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Doctor Name</Typography>
                    <SelectTag menus={doctorDropdown} formControlSX={{ flex: 0.8 }} name="doctor" disabled={!isFieldEditable} />
                  </Stack>
                </Grid>

                {/* Column 3 */}
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Date / Time</Typography>
                    <TextField sx={{ flex: 0.4 }} name="date" type="date" disabled={!isEnabledDate || !['new', 'edit'].includes(currentAction)} />
                    <TextField sx={{ flex: 0.4 }} name="time" type="time" disabled={!isEnabledDate || !['new', 'edit'].includes(currentAction)} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Guardian</Typography>
                    <SelectTag formControlSX={{ flex: 0.2 }} name="guardian_type" disabled={!isFieldEditable} menus={guardianDropdown} />
                    <TextField sx={{ flex: 0.6 }} name="guardian_name" disabled={!isFieldEditable} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Address</Typography>
                    <TextField sx={{ flex: 0.8 }} name="address" disabled={!isFieldEditable} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Ref By</Typography>
                    <SelectTag disabled={!isFieldEditable} formControlSX={{ flex: 0.8 }} name="ref_by" menus={referralDropdown.map((d) => ({ ...d, name: d.referral_name }))} />
                  </Stack>
                </Grid>

                {/* Services table */}
                <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button variant="outlined" disabled={!['new', 'edit'].includes(currentAction)} onClick={() => setAddDetailsDialogOpen(true)}>
                      Add Details
                    </Button>
                  </Stack>
                  <Table
                    containerSX={{ flex: 1 }}
                    data={detailList.map((d, i) => ({ _id: i, ...d }))}
                    columns={BILL_TABLE_HEADER}
                    size="small"
                    showEditButton={['new', 'edit'].includes(currentAction)}
                    handleDeleteRow={(idx) => setDetailList((prev) => prev.filter((_, i) => i !== idx))}
                  />
                </Grid>

                {/* Billing panel */}
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Payment Mode</Typography>
                    <SelectTag disabled={!['new', 'edit'].includes(currentAction)} formControlSX={{ flex: 0.8 }} name="bill.pay_mode" menus={RECEIPT_MODE} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Auth Code</Typography>
                    <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="bill.auth_code" placeholder="Auth Code" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Total Qty / Amt</Typography>
                    <TextField disabled sx={{ flex: 0.3 }} name="bill.total_qty" placeholder="Qty" />
                    <TextField disabled sx={{ flex: 0.5 }} name="bill.total_amt" placeholder="Amt" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Discount</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction) || !watch('bill.pay_mode')}
                      formControlSX={{ flex: 0.3 }}
                      value={discountType}
                      onChange={(e) => { setDiscountType(e.target.value); setValue('bill.disc_amt', 0); }}
                      menus={DISCOUNT_TYPES}
                      name="discountType"
                    />
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction) || !watch('bill.pay_mode')}
                      sx={{ flex: 0.5 }}
                      name="bill.disc_amt"
                      placeholder={discountType === 'percentage' ? 'Disc. %' : 'Disc. Amt'}
                      type="number"
                      inputProps={{ min: '0', max: discountType === 'percentage' ? '100' : undefined }}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Net Amt</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="bill.net_amt" placeholder="Net Amount" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>RecAmt 1&2</Typography>
                    <TextField sx={{ flex: 0.4 }} disabled={!['new', 'edit'].includes(currentAction)} type="number" inputProps={{ min: '0' }} name="bill.rec_amt_1" placeholder="Amt 1" />
                    <TextField sx={{ flex: 0.4 }} disabled={!['new', 'edit'].includes(currentAction)} type="number" inputProps={{ min: '0' }} name="bill.rec_amt_2" placeholder="Amt 2" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Total Recpt.</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="bill.total_recpt" placeholder="Total" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Balance Amt</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="bill.balance_amt" placeholder="Balance Amount" />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>

      {/* Patient Search Dialog */}
      <Dialog PaperProps={{ sx: { width: '80%', p: 2 } }} open={searchPatientDialog} onClose={() => setSearchPatientDialog(false)} maxWidth="xl" scroll="body">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" borderBottom="1px solid" borderColor="grey.300" pb={1}>
          <Typography variant="h4" fontWeight="bold">Patient Query Window</Typography>
          <IconButton onClick={() => setSearchPatientDialog(false)}><CloseIcon color="error" fontSize="large" /></IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={PATIENT_TABLE_HEADER}
          data={patientDetailList}
          onRowClick={(row) => {
            setValue('uhid', row.uhid);
            setValue('patient_name', `${row.first_name || ''} ${row.last_name || ''}`.trim());
            setValue('patient_suffix', row.name_suffix || 'Mr.');
            setValue('mobile', row.address?.mobile || '');
            setValue('gender', row.gender || 'Male');
            setValue('address', row.address?.address || '');
            setValue('doctor', row.consultant?._id || '');
            setValue('ref_by', row.ref_by?._id || '');
            setValue('sponsor', row.sponsor?._id || '');
            setValue('guardian_name', row.guardian_name || '');
            setValue('guardian_type', row.guardian_type?._id || '');
            if (row.dob) {
              const age = AgeCalculator.calculatePreciseAge(row.dob);
              setValue('age', age?.displayText || '');
              setValue('age_in_days', age?.totalDays || 0);
            }
            setPatientType('OPD');
            setSearchPatientDialog(false);
          }}
        />
      </Dialog>

      {/* IPD Search Dialog */}
      <Dialog PaperProps={{ sx: { p: 1 } }} open={IPDSearchDialogOpen} onClose={() => setIPDSearchDialogOpen(false)} scroll="body" maxWidth="xl">
        <IPDSearchHandler key={String(IPDSearchDialogOpen)} onClose={() => setIPDSearchDialogOpen(false)} onAdd={searchIPDDetails} />
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={addDetailsDialogOpen} onClose={() => setAddDetailsDialogOpen(false)} PaperProps={{ sx: { width: '50%', height: '70vh', p: 2 } }} maxWidth="xl" scroll="body">
        <PhysiotherapyBookingDetailForm
          onClose={() => setAddDetailsDialogOpen(false)}
          onAdd={(data) => {
            const exists = detailList.find(({ service }) => service === data.service);
            if (exists) { toast('Service already added', { variant: 'error' }); return; }
            setDetailList((prev) => [...prev, data]);
            toast('Service added', { variant: 'success' });
          }}
        />
      </Dialog>

      {/* Query Dialog */}
      <Dialog open={openQueryDialog} onClose={() => setOpenQueryDialog(false)} PaperProps={{ sx: { width: '80%', p: 2 } }} maxWidth="xl" scroll="body">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" borderBottom="1px solid" borderColor="grey.300" pb={1}>
          <Typography variant="h4" fontWeight="bold">Physiotherapy Booking Query Window</Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}><CloseIcon color="error" fontSize="large" /></IconButton>
        </Stack>
        <MuiTextField
          sx={{ mt: 1, width: '100%', '& .MuiOutlinedInput-root': { padding: '0', fontSize: '14px' }, '& .MuiOutlinedInput-input': { padding: '8px 14px' } }}
          label="Search Patient"
          variant="outlined"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <Table
          sx={{ mt: 0.5 }}
          columns={QUERY_TABLE_HEADER}
          data={bookingDataObj.data}
          loading={bookingDataObj.loading}
          onRowClick={(row) => { setSelectedRecord(row); setOpenQueryDialog(false); }}
        />
      </Dialog>
    </>
  );
};
